import { apiRequest } from './api';
import { Log, LogFilters, LogStats, ApiResponse, PaginationParams } from '../types';

export const logService = {
  // Buscar todos os logs com filtros e paginação
  async getLogs(params?: LogFilters & PaginationParams): Promise<ApiResponse<Log[]>> {
    const queryParams = new URLSearchParams();
    
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.tipo) queryParams.append('tipo', params.tipo);
    if (params?.acao) queryParams.append('acao', params.acao);
    if (params?.usuario_id) queryParams.append('usuario_id', params.usuario_id.toString());
    if (params?.entidade_tipo) queryParams.append('entidade_tipo', params.entidade_tipo);
    if (params?.entidade_id) queryParams.append('entidade_id', params.entidade_id.toString());
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const url = `/logs${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return apiRequest.get<Log[]>(url);
  },

  // Buscar log por ID
  async getLogById(id: number): Promise<ApiResponse<Log>> {
    return apiRequest.get<Log>(`/logs/${id}`);
  },

  // Buscar logs por entidade (cliente, contrato, etc.)
  async getLogsByEntity(
    tipo: string,
    entidade_id: number,
    params?: PaginationParams & { startDate?: string; endDate?: string }
  ): Promise<ApiResponse<Log[]>> {
    const queryParams = new URLSearchParams();
    
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const url = `/logs/entity/${tipo}/${entidade_id}${
      queryParams.toString() ? `?${queryParams.toString()}` : ''
    }`;
    
    return apiRequest.get<Log[]>(url);
  },

  // Buscar logs por usuário
  async getLogsByUser(
    usuario_id: number,
    params?: { tipo?: string; acao?: string } & PaginationParams
  ): Promise<ApiResponse<Log[]>> {
    const queryParams = new URLSearchParams();
    
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.tipo) queryParams.append('tipo', params.tipo);
    if (params?.acao) queryParams.append('acao', params.acao);

    const url = `/logs/user/${usuario_id}${
      queryParams.toString() ? `?${queryParams.toString()}` : ''
    }`;
    return apiRequest.get<Log[]>(url);
  },

  // Buscar logs por tipo
  async getLogsByType(
    tipo: string,
    params?: { acao?: string; startDate?: string; endDate?: string } & PaginationParams
  ): Promise<ApiResponse<Log[]>> {
    const queryParams = new URLSearchParams();
    
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.acao) queryParams.append('acao', params.acao);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const url = `/logs/type/${tipo}${
      queryParams.toString() ? `?${queryParams.toString()}` : ''
    }`;
    return apiRequest.get<Log[]>(url);
  },

  // Buscar estatísticas de logs
  async getLogStats(params?: { startDate?: string; endDate?: string }): Promise<ApiResponse<LogStats>> {
    const queryParams = new URLSearchParams();
    
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const url = `/logs/stats${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return apiRequest.get<LogStats>(url);
  },

  // Criar log (usado internamente pelo sistema)
  async createLog(logData: Partial<Log>): Promise<ApiResponse<Log>> {
    return apiRequest.post<Log>('/logs', logData);
  },

  // Deletar todos os logs
  async deleteAllLogs(): Promise<ApiResponse<{ deletedCount: number }>> {
    return apiRequest.delete<{ deletedCount: number }>('/logs/all');
  },

  // Utilitários para formatação
  formatLogType(tipo: string): string {
    const types: Record<string, string> = {
      cliente: 'Cliente',
      contrato: 'Contrato',
      fatura: 'Fatura',
      sistema: 'Sistema',
      autenticacao: 'Autenticação'
    };
    return types[tipo] || tipo;
  },

  formatLogAction(acao: string): string {
    const actions: Record<string, string> = {
      criacao: 'Criação',
      edicao: 'Edição',
      exclusao: 'Exclusão',
      pagamento: 'Pagamento',
      cancelamento: 'Cancelamento',
      autenticacao: 'Autenticação',
      logout: 'Logout',
      bloqueio: 'Bloqueio',
      ativacao: 'Ativação',
      desativacao: 'Desativação',
      outros: 'Outros'
    };
    return actions[acao] || acao;
  },

  getActionColor(acao: string): string {
    const colors: Record<string, string> = {
      criacao: 'success',
      edicao: 'warning',
      exclusao: 'error',
      pagamento: 'primary',
      cancelamento: 'error',
      autenticacao: 'info',
      logout: 'info',
      bloqueio: 'error',
      ativacao: 'success',
      desativacao: 'warning',
      outros: 'default'
    };
    return colors[acao] || 'default';
  },

  getTypeColor(tipo: string): string {
    const colors: Record<string, string> = {
      cliente: 'primary',
      contrato: 'secondary',
      fatura: 'warning',
      sistema: 'info',
      autenticacao: 'success'
    };
    return colors[tipo] || 'default';
  }
};

export default logService; 